
import { http } from '@services/http';

/**
 * Report a browser-side crash to the same place server failures land.
 *
 * The server half of error handling was complete: a failure mints a trace id,
 * logs the scrubbed detail against it, writes an msi_error_logs row, and
 * returns the id — so a screenshot is enough for support to find the entry.
 *
 * The client half did none of that. A render crash reached RouteErrorBoundary,
 * went to console.error, and stopped. No row, no reference, and nothing for
 * the user to quote beyond describing what they saw.
 *
 * Returns the trace id so the boundary can show it. Resolves to null rather
 * than throwing: this runs at the moment the UI has already failed, and an
 * error boundary that itself throws leaves a blank page.
 */
/**
 * Signatures already reported since this tab loaded, and the id each one got.
 *
 * A render loop is the reason this matters. The 2026-08-21 incident threw
 * "Maximum update depth exceeded" several times a second: the tree unmounted,
 * the router remounted it, and it threw again. Each pass builds a NEW boundary
 * instance, so an instance-level flag would not stop it — this has to live
 * outside the component. Without it a loop would fire dozens of requests a
 * second and hit the server's throttle, which returns 429 and leaves the user
 * with no reference at all.
 *
 * The cached id is returned to later callers so a repeat still shows a
 * reference — the same one, which is correct: it is the same row.
 *
 * Digits are stripped from the message for the same reason the backend strips
 * them before hashing: a loop that fails on row 3 then row 4 is one bug.
 */
const reported = new Map();

const signatureOf = (error) =>
  [
    error?.name ?? 'Error',
    String(error?.message ?? '').replace(/\d+/g, 'N'),
    window.location.pathname,
  ].join('|');

export async function reportClientError(error, info = {}) {
  const signature = signatureOf(error);
  if (reported.has(signature)) return reported.get(signature);
  // Claimed before the request goes out, not after it resolves — a loop fires
  // again well before the first response comes back.
  reported.set(signature, null);

  try {
    const { data } = await http.post(
      '/api/client-errors',
      {
        name: error?.name ?? 'Error',
        message: String(error?.message ?? error ?? ''),
        stack: String(error?.stack ?? ''),
        componentStack: String(info?.componentStack ?? ''),
        // The route, not the full URL, is what groups these usefully — the
        // backend hashes on it, so /customers/1 and /customers/2 crashing the
        // same way file as one row rather than one per customer.
        route: window.location.pathname,
        url: window.location.href,
      },
      // The interceptor must not redirect on a 401 here. The boundary may be
      // rendering *because* the session died, and bouncing to /login would
      // replace the crash screen with a login form and lose the reference.
      { meta: { silent: true } },
    );
    const traceId = data?.trace_id ?? null;
    reported.set(signature, traceId);
    return traceId;
  } catch {
    // Swallowed deliberately. If reporting fails the user still gets the
    // recovery screen; they just have no reference to quote.
    //
    // The signature stays claimed rather than being released for a retry. A
    // report fails because the network or the server is down, and a loop
    // retrying it every frame makes both worse.
    return null;
  }
}

/** Test seam — the module-level cache would otherwise leak between cases. */
export function __resetReportedClientErrors() {
  reported.clear();
}

export default reportClientError;
